import { useCallback, useContext, useEffect, useRef } from "react";
import { AudioEditorContext } from "../AudioEditor/AudioEditorContext";
import { useDispatch, useSelector } from "react-redux";
import { surferHooks } from "..";
import { v1 as uuid } from 'uuid';
import { useQuery } from "@tanstack/react-query";
import { wavesurferToTime } from "utils/formatter";
import { getRegionsFetch } from "store/middlewares/region.crud";
import { markingWordsHooks } from "pages/Admin/TranscriptionSystem/MarkingWords";
import Wavesurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import qc from "keys/queryClient";
import { useParams } from "react-router-dom";


const useAudioEffects = (stopAudioAfterSeconds) => {

    markingWordsHooks.useGetAudioNeto()

    const onPause = surferHooks.useOnPause()
    const onPlayPause = surferHooks.useOnPlayPause()

    const params = useParams()
    let assessmentID = params.assessmentID

    const { refs, state } = useContext(AudioEditorContext)

    const { firstDoubleClickTimeRef, tempRegionRef, containerRef, regionsPluginRef, wavesurferRef } = refs || {}
    const { isAudioMarkingMode, isNetoAudio, audioRate, setSelectedRegion, isLoading, setMenuPosition, setIsWaitingForSecondClick, setRegionsList, setAudioDuration, setIsLoading } = state || {}

    const { ignoreActions } = useSelector(state => state.wavesurfer);
    const { assessment } = useSelector(state => state.assessment);

    const basicRegion = {
        color: "rgba(126, 138, 158, 0.10)",
        drag: true,
        resize: true,
    }

    const isNetoAudioRef = useRef(isNetoAudio)
    const isAudioMarkingModeRef = useRef(isAudioMarkingMode);
    const stopAudioAfterSecondsRef = useRef()

    useEffect(() => {
        isNetoAudioRef.current = isNetoAudio;
    }, [isNetoAudio])

    useEffect(() => {
        isAudioMarkingModeRef.current = isAudioMarkingMode;
    }, [isAudioMarkingMode]);

    const regions = useQuery({
        queryKey: [qc.regions, assessmentID],
        queryFn: async () => {
            const { data } = await getRegionsFetch(assessment.id)
            setRegionsList(data)
            return data
        },
        enabled: !!assessment.id
    })

    const onMount = surferHooks.useOnMount();
    const borderColor = '#5F6E86';

    const updateRegionsState = useCallback(() => {
        if (!regionsPluginRef.current) return;
        const currentRegions = regionsPluginRef.current.getRegions();
        const validRegions = currentRegions.filter(r => r.id !== 'temp-marker');
        setRegionsList(validRegions.map(r => ({ id: r.id, start: r.start, end: r.end })));
    }, []);

    const handleWaveformDoubleClick = useCallback((clickedTime) => {

        if (firstDoubleClickTimeRef.current === null) {
            firstDoubleClickTimeRef.current = clickedTime;
            setIsWaitingForSecondClick(true);

            if (regionsPluginRef.current) {
                const tempReg = regionsPluginRef.current.addRegion({
                    id: 'temp-marker',
                    start: clickedTime,
                    end: clickedTime,
                    color: borderColor,
                    drag: false,
                    resize: false,
                });

                setTimeout(() => {
                    if (tempReg && tempReg.element) {
                        tempReg.element.style.borderLeft = `1px solid ${borderColor}`;
                    }
                }, 10);

                tempRegionRef.current = tempReg;
            }
        } else {
            const prevTime = firstDoubleClickTimeRef.current;
            const startTime = Math.min(prevTime, clickedTime);
            const endTime = Math.max(prevTime, clickedTime);

            if (regionsPluginRef.current) {
                if (tempRegionRef.current) {
                    tempRegionRef.current.remove();
                    tempRegionRef.current = null;
                }

                const newReg = regionsPluginRef.current.addRegion({
                    id: uuid(),
                    start: startTime,
                    end: endTime,
                    ...basicRegion
                });

                setTimeout(() => {
                    if (newReg && newReg.element) {
                        newReg.element.style.borderLeft = `1px solid ${borderColor}`;
                        newReg.element.style.borderRight = `1px solid ${borderColor}`;
                    }
                }, 10);
            }

            firstDoubleClickTimeRef.current = null;
            setIsWaitingForSecondClick(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoading && regionsPluginRef.current && regions.data) {
            // מומלץ לנקות קודם כדי למנוע כפילויות במקרה של רינדור מחדש
            regionsPluginRef.current.clearRegions();

            // אם אנחנו במצב נטו, אנחנו עוצרים כאן והמסך נשאר נקי ממקטעים
            if (isNetoAudio || !isAudioMarkingMode) return;

            regions.data.forEach((regionData) => {
                const newReg = regionsPluginRef.current.addRegion({
                    id: regionData.id,
                    start: parseFloat(regionData.start_time),
                    end: parseFloat(regionData.end_time),
                    ...basicRegion
                });

                setTimeout(() => {
                    if (newReg && newReg.element) {
                        newReg.element.style.borderLeft = `1px solid ${borderColor}`;
                        newReg.element.style.borderRight = `1px solid ${borderColor}`;
                    }
                }, 10);

            });
        }
    }, [isLoading, regions.data, regionsPluginRef, isNetoAudio, isAudioMarkingMode]);


    useEffect(() => {
        if (!containerRef.current || !assessment.audio_file) return;

        const regionsPlugin = RegionsPlugin.create();
        regionsPluginRef.current = regionsPlugin;

        const timelinePlugin = TimelinePlugin.create({
            timeInterval: 0.1,
            primaryLabelInterval: 1,
            style: {
                color: "#838386"
            }
        });

        const ws = Wavesurfer.create({
            container: containerRef.current,
            waveColor: '#bfbfc5',
            progressColor: '#2D5B88',
            height: 50,
            normalize: true,
            minPxPerSec: 70,
            rate: audioRate,
            plugins: [regionsPlugin, timelinePlugin],
        });

        wavesurferRef.current = ws;

        onMount(ws, stopAudioAfterSeconds)

        ws.on('ready', () => {
            const currentDuration = ws.getDuration();
            setAudioDuration(wavesurferToTime(currentDuration));
            setIsLoading(false)
            stopAudioAfterSecondsRef.current = false
        });

        wavesurferRef.current.on('audioprocess', function () {

            const currentTime = wavesurferRef.current.getCurrentTime()

            if (Math.floor(currentTime) === 60) {
                if (!stopAudioAfterSecondsRef.current) {

                    onPause()

                    stopAudioAfterSecondsRef.current = true
                }
            }

        })

        ws.on('dblclick', (relativeX) => {
            // פועל רק במצב חיתוך אודיו פעיל
            if (isNetoAudioRef.current || !isAudioMarkingModeRef.current) return;

            const duration = ws.getDuration();
            const clickedTime = relativeX * duration;
            handleWaveformDoubleClick(clickedTime);
        });

        ws.on('finish', () => {
            onPause()
        });

        regionsPlugin.on('region-created', updateRegionsState);
        regionsPlugin.on('region-updated', updateRegionsState);
        regionsPlugin.on('region-removed', updateRegionsState);

        regionsPlugin.on('region-clicked', (region, e) => {
            e.stopPropagation();
            setMenuPosition({ top: e.clientY, left: e.clientX });
            setSelectedRegion(region);
        });

        return () => {
            ws.destroy();
        };
    }, [assessment.audio_file, audioRate]);

    const fileToLoad = isNetoAudio ? assessment.neto_audio_file : assessment.audio_file;

    useEffect(() => {
        if (!wavesurferRef.current || !fileToLoad) return;

        const loadAudioSafely = async () => {
            setIsLoading(true);

            if (wavesurferRef.current.isPlaying()) {
                onPause()
            }

            try {
                let safeUrlToLoad = fileToLoad;

                if (fileToLoad.startsWith('blob:')) {
                    const response = await fetch(fileToLoad);
                    const blob = await response.blob();
                    safeUrlToLoad = URL.createObjectURL(blob);
                }

                await wavesurferRef.current.load(safeUrlToLoad);

            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('WaveSurfer load error:', err);
                    setIsLoading(false);
                }
            }
        };

        loadAudioSafely();

    }, [fileToLoad, wavesurferRef, setIsLoading]);

    useEffect(() => {
        const handleKeyPress = (event) => {
            // מניעת הפעלת קיצורי דרך אם המערכת במצב שבו צריך להתעלם מפעולות (למשל מודאל פתוח)
            if (ignoreActions) return;

            // מוודאים ש-WaveSurfer מאותחל
            if (!wavesurferRef.current) return;

            switch (event.code) {
                case 'Space':
                    event.preventDefault(); // מונע את גלילת העמוד כלפי מטה בלחיצה על רווח
                    onPlayPause()
                    break;
                case 'ArrowLeft':
                    event.preventDefault(); // מונע גלילה אופקית במידה ויש
                    wavesurferRef.current.skip(-2);
                    break;
                case 'ArrowRight':
                    event.preventDefault();
                    wavesurferRef.current.skip(2);
                    break;
                case 'KeyZ':
                    event.preventDefault();
                    
                    // פועל רק במצב חיתוך אודיו פעיל
                    if (isNetoAudioRef.current || !isAudioMarkingModeRef.current) return;

                    // מוודאים שאנחנו לא במצב צפייה נטו ושהעריכה אכן מאופשרת
                    if (!isNetoAudioRef.current && isAudioMarkingModeRef.current) {
                        const currentTime = wavesurferRef.current.getCurrentTime();
                        handleWaveformDoubleClick(currentTime);
                    }
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyPress);

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [ignoreActions]);

}

export default useAudioEffects